import { baseClient } from './baseRequest';
import { Section } from '../types';

export type SocialMedia = {
  link: string;
  network: string;
};

export type ArtistProfile = {
  coverUrl?: string;
  customSections: Section[];
  draftCustomSections?: Section[] | null;
};

export type User = {
  id: string;
  email: string;
  name: string;
  description: string;
  title: string;
  photoUrl: string;
  country: string;
  socialMedia: SocialMedia[];
  studioContract: {
    accepted: boolean;
    signedTimestamp: number | null;
  };
  artistProfile?: ArtistProfile;
  type?: string;
  metadata?: {
    collaboratorId?: string;
    defaultPlanId?: string | null;
  };
};

export type SearchUserInput = {
  query: string;
  options: { limit: number; skip: number };
  searchFields: string[];
};

export type SearchUserOutput = {
  id: string;
  name: string;
  email?: string;
};

export type UserProperties = {
  b2b: boolean;
};

export type UserApiClient = {
  getUser: () => Promise<User | null>;
  upsertUser: (user: Partial<User>) => Promise<User>;
  acceptStudioContract: () => Promise<User>;
  search: (input: SearchUserInput) => Promise<SearchUserOutput[]>;
  impersonate: (customUserId: string) => Promise<string>;
  stopImpersonating: () => Promise<string>;
  addClockToUser: (clockSerial: string) => Promise<void>;
  removeClockFromUser: () => Promise<void>;
  sendKlydoToClock: (klydoId: string) => Promise<void>;
  getUserProperties: () => Promise<UserProperties>;
};

const userApiClient: UserApiClient = {
  getUser: () => baseClient.get<User>({ url: `/studio/user` }),
  upsertUser: (user: Partial<User>) =>
    baseClient.put<User>({ url: `/studio/user`, body: user }),
  acceptStudioContract: () =>
    baseClient.post<User>({
      url: `/studio/user/accept-contract`,
    }),
  search: (input: SearchUserInput) =>
    baseClient.post<SearchUserOutput[]>({
      url: '/admin/users/.search',
      body: input,
    }),
  impersonate: (customUserId: string) =>
    baseClient.post<string>({
      url: `/admin/users/impersonate/${customUserId}`,
    }),
  stopImpersonating: () =>
    baseClient.post<string>({
      url: '/admin/users/stop-impersonating',
    }),
  addClockToUser: (clockSerial: string) =>
    baseClient.post<void>({
      url: `/studio/user/add-clock`,
      body: { clockSerial },
    }),
  removeClockFromUser: () =>
    baseClient.post<void>({
      url: `/studio/user/remove-clock`,
    }),
  sendKlydoToClock: (klydoId: string) =>
    baseClient.post<void>({
      url: `/studio/user/send-klydo-to-clock`,
      body: { klydoId },
    }),
  getUserProperties: () =>
    baseClient.get<UserProperties>({
      url: `/studio/user/properties`,
    }),
};

export default userApiClient;
